import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { useForm, useFieldArray } from 'react-hook-form';
import { Plus, Trash2, Calculator } from 'lucide-react';
import toast from 'react-hot-toast';

const CreateInvoice = () => {
  const { state, dispatch } = useApp();
  const { customers } = state;
  const navigate = useNavigate();
  const { register, control, handleSubmit, watch, setValue, formState: { errors } } = useForm({
    defaultValues: {
      workRows: [{ workName: '', quantity: 1, pricePerUnit: 0 }]
    }
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'workRows'
  });

  const watchedFields = watch();
  const workRows = watchedFields.workRows || [];
  const paidAmount = parseFloat(watchedFields.paidAmount) || 0;

  // Calculate subtotal
  const subtotal = workRows.reduce((sum, row) => {
    const quantity = parseFloat(row.quantity) || 0;
    const price = parseFloat(row.pricePerUnit) || 0;
    return sum + (quantity * price);
  }, 0);

  const remainingAmount = subtotal - paidAmount;

  const addWorkRow = () => {
    append({ workName: '', quantity: 1, pricePerUnit: 0 });
  };

  const removeWorkRow = (index) => {
    if (fields.length > 1) {
      remove(index);
    }
  };

  const onSubmit = (data) => {
    const invoiceData = {
      ...data,
      subtotal,
      remainingAmount,
      status: remainingAmount > 0 ? 'Pending' : 'Paid',
      createdAt: new Date().toISOString()
    };

    dispatch({ type: 'ADD_INVOICE', payload: invoiceData });
    toast.success('Invoice created successfully!');
    navigate('/invoice-history');
  };

  const fillCustomerData = (customerId) => {
    const customer = customers.find(c => c.id === parseInt(customerId));
    if (customer) {
      setValue('customerName', customer.name);
      setValue('customerMobile', customer.mobile);
      setValue('customerEmail', customer.email || '');
    }
  };

  return (
    <div className="p-8">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold gradient-text mb-2">Create Invoice</h1>
          <p className="text-gray-600 text-lg">Generate a new invoice for your customer</p>
        </div>

        <div className="card">
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {/* Customer Selection */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Existing Customer (Optional)
              </label>
              <select
                onChange={(e) => fillCustomerData(e.target.value)}
                className="input-field"
              >
                <option value="">Select a customer...</option>
                {customers.map((customer) => (
                  <option key={customer.id} value={customer.id}>
                    {customer.name} - {customer.mobile}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Customer Details */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Customer Name *
              </label>
              <input
                type="text"
                {...register('customerName', { required: 'Customer name is required' })}
                className="input-field"
                placeholder="Enter customer name"
              />
              {errors.customerName && (
                <p className="text-red-500 text-sm mt-1">{errors.customerName.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Mobile Number *
              </label>
              <input
                type="tel"
                {...register('customerMobile', { required: 'Mobile number is required' })}
                className="input-field"
                placeholder="Enter mobile number"
              />
              {errors.customerMobile && (
                <p className="text-red-500 text-sm mt-1">{errors.customerMobile.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email Address
              </label>
              <input
                type="email"
                {...register('customerEmail')}
                className="input-field"
                placeholder="Enter email address"
              />
            </div>
          </div>

          {/* Work Rows */}
          <div>
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">Work Details</h3>
              <button
                type="button"
                onClick={addWorkRow}
                className="btn-primary"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Work Row
              </button>
            </div>

            <div className="space-y-4">
              {fields.map((field, index) => (
                <div key={field.id} className="grid grid-cols-1 md:grid-cols-5 gap-4 p-4 bg-gray-50 rounded-lg">
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Work Name *
                    </label>
                    <input
                      type="text"
                      {...register(`workRows.${index}.workName`, { required: 'Work name is required' })}
                      className="input-field"
                      placeholder="Enter work description"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Quantity *
                    </label>
                    <input
                      type="number"
                      min="1"
                      step="0.01"
                      {...register(`workRows.${index}.quantity`, { required: 'Quantity is required' })}
                      className="input-field"
                      placeholder="Qty"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Price per Unit *
                    </label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      {...register(`workRows.${index}.pricePerUnit`, { required: 'Price is required' })}
                      className="input-field"
                      placeholder="Price"
                    />
                  </div>

                  <div className="flex items-end">
                    <div className="w-full">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Total
                      </label>
                      <div className="input-field bg-gray-100">
                        ₹{((parseFloat(workRows[index]?.quantity) || 0) * (parseFloat(workRows[index]?.pricePerUnit) || 0)).toFixed(2)}
                      </div>
                    </div>
                    {fields.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeWorkRow(index)}
                        className="ml-2 p-2 text-red-600 hover:text-red-800"
                      >
                        <Trash2 size={16} />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Payment Summary */}
          <div className="bg-blue-50 p-6 rounded-lg">
            <div className="flex items-center mb-4">
              <Calculator className="w-5 h-5 text-blue-600 mr-2" />
              <h3 className="text-lg font-medium text-gray-900">Payment Summary</h3>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Subtotal
                </label>
                <div className="input-field bg-gray-100 text-lg font-semibold">
                  ₹{(subtotal || 0).toFixed(2)}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Paid Amount *
                </label>
                <input
                  type="number"
                  min="0"
                  max={subtotal}
                  step="0.01"
                  {...register('paidAmount', { 
                    required: 'Paid amount is required',
                    min: { value: 0, message: 'Paid amount cannot be negative' },
                    max: { value: subtotal, message: 'Paid amount cannot exceed subtotal' }
                  })}
                  className="input-field"
                  placeholder="Enter paid amount"
                />
                {errors.paidAmount && (
                  <p className="text-red-500 text-sm mt-1">{errors.paidAmount.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Remaining Amount
                </label>
                <div className={`input-field text-lg font-semibold ${
                  (remainingAmount || 0) > 0 ? 'bg-red-50 text-red-600' : 'bg-green-50 text-green-600'
                }`}>
                  ₹{(remainingAmount || 0).toFixed(2)}
                </div>
              </div>
            </div>
          </div>

          <div className="flex space-x-4">
            <button
              type="submit"
              className="btn-primary"
            >
              Create Invoice
            </button>
            <button
              type="button"
              onClick={() => navigate('/invoice-history')}
              className="btn-secondary"
            >
              Cancel
            </button>
          </div>
        </form>
        </div>
      </div>
    </div>
  );
};

export default CreateInvoice;
